import React, { useEffect, useState } from "react";
import { getFileUrl } from "../../core/config/api.js";
import { useEntitlement } from "../../contexts";
import { Avatar, Badge, Icon, Tabs, Tooltip } from "../../components/ui";
import { statusMeta } from "./Row.jsx";

import OverviewTab from "./OverviewTab.jsx";
import OrderList from "./OrderList.jsx";
import KycTab from "./KycTab.jsx";
import NotesTab from "./NotesTab.jsx";
import ActivityTab from "./ActivityTab.jsx";
import { UserRequests } from "./UserRequests";

const formatUserType = (type) => {
  if (!type) return "Unknown";
  return type
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// A contact ACTION — mail the person, call them. Only things you can actually do live here now:
// status and type moved out to real badges, because rendering them as identical grey circles
// meant you could not tell a blocked account from a verified one without hovering each one.
// `href` is required: a chip that does nothing is a tooltip wearing a button's clothes.
const MetaChip = ({ label, value, icon, href }) => (
  <Tooltip content={`${label}: ${value}`}>
    <a className="cdr__meta-chip" href={href} aria-label={`${label}: ${value}`}>
      {icon}
    </a>
  </Tooltip>
);

const TAB_ICON = {
  overview: "User",
  kyc: "ShieldCheck",
  notes: "FileText",
  activity: "Activity",
  requests: "ShoppingCart",
  orders: "Receipt",
};

const UserDetailPanel = ({
  contact,
  onContactUpdated,
}) => {
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    setActiveTab("overview");
  }, [contact?.id]);

  // KYC ID verification (Feature 29): the tab disappears with the plan.
  const hasKyc = useEntitlement("kyc_verification");

  const tabs = [
    { id: "overview", label: "Contact",     component: OverviewTab   },
    ...(hasKyc ? [{ id: "kyc", label: "KYC", component: KycTab }] : []),
    { id: "notes",    label: "Notes",       component: NotesTab      },
    { id: "activity", label: "Activity",    component: ActivityTab   },
    { id: "requests", label: "Requests",    component: UserRequests  },
    { id: "orders",   label: "Cart Orders", component: OrderList     },
  ];

  const contentProps = {
    user: contact,
    afterSubmit: onContactUpdated,
  };

  const ContentComponent = tabs.find((t) => t.id === activeTab)?.component;
  const fullName = [contact.first_name, contact.last_name].filter(Boolean).join(" ") || contact.email || `#${contact.id}`;
  const status = statusMeta(contact);
  const emailValue = contact.email || "No email on file";
  const typeValue = formatUserType(contact.type);

  return (
    <div className="cdr cdr--panel">
      <div className="cdr__hero">
        <Avatar
          name={fullName}
          src={contact.avatar_file_id ? getFileUrl(contact.avatar_file_id) : undefined}
          size="xl"
        />

        <div className="cdr__hero-id">
          <h3 className="cdr__hero-name">{fullName}</h3>
          <p className="cdr__hero-sub">ID {contact.id}</p>

          {/* Only reachable actions. A person with no email gets no mail chip rather than a
              dead one, which is why each is rendered conditionally. */}
          <div className="cdr__hero-actions" role="group" aria-label="Contact actions">
            {contact.email ? (
              <MetaChip label="Email" value={emailValue} icon={<Icon name="Mail" size="sm" />} href={`mailto:${contact.email}`} />
            ) : null}
            {contact.phone ? (
              <MetaChip label="Phone" value={contact.phone} icon={<Icon name="Phone" size="sm" />} href={`tel:${contact.phone}`} />
            ) : null}
          </div>
        </div>

        {/* Status and type read at a glance, tone-coded through the shared status map — the
            same tone the person's row in the directory already uses. */}
        <div className="cdr__hero-badges">
          <Badge status={status.tone} dot>{status.label}</Badge>
          <Badge tone="muted">{typeValue}</Badge>
        </div>
      </div>

      <Tabs
        variant="underline"
        fullWidth
        aria-label="Profile sections"
        items={tabs.map(({ id, label }) => ({
          value: id,
          label,
          icon: <Icon name={TAB_ICON[id]} size="xs" />,
        }))}
        value={activeTab}
        onChange={setActiveTab}
      />

      <div className="cdr__content cdr__content--full">
        {ContentComponent ? <ContentComponent {...contentProps} /> : null}
      </div>
    </div>
  );
};

export default UserDetailPanel;
